import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/primitives';
import { cn } from '@/lib/utils';

/* A minimal, token-styled, accessible create modal reused by the SEO "Add
 * domain" and Sales "Add lead" flows (design-spec §7 minimal / §9 denials read
 * as human guidance). Centered overlay, Esc + backdrop close, labelled form,
 * submit disabled while pending, visible inline error. Focus lands on the first
 * field on open. The parent owns success (close + refetch + notice); this
 * component owns pending + error state and resets itself each time it opens. */

export interface DialogField {
  name: string;
  label: string;
  type?: 'text' | 'email' | 'url';
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
  hint?: string;
}

export function CreateDialog({
  open,
  onClose,
  title,
  description,
  fields,
  submitLabel = 'Create',
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  fields: DialogField[];
  submitLabel?: string;
  /** Resolve on success (parent then closes); throw an Error whose `message`
   *  is rendered inline as human guidance. */
  onSubmit: (values: Record<string, string>) => Promise<void>;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const titleId = useId();
  const descId = useId();

  // Reset + focus the first field whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    setValues({});
    setError(null);
    setPending(false);
    const t = setTimeout(() => firstFieldRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  // Esc to close (unless a submit is in flight).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !pending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, pending]);

  if (!open) return null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await onSubmit(values);
      // On success the parent closes this dialog (open → false), which unmounts
      // and resets state; no need to clear pending here.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setPending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:items-center">
      <div
        className="absolute inset-0 bg-scrim/40 backdrop-blur-[1px] animate-fade-in"
        onClick={() => !pending && onClose()}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        className="relative z-10 mt-16 w-full max-w-md animate-fade-in rounded-xl border border-border-default bg-surface shadow-lg sm:mt-0"
      >
        <header className="flex items-start justify-between border-b border-border-subtle px-5 py-3">
          <div>
            <h2 id={titleId} className="text-sm font-semibold text-text-primary">
              {title}
            </h2>
            {description && (
              <p id={descId} className="mt-0.5 text-xs text-text-secondary">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => !pending && onClose()}
            className="rounded-sm p-1 text-text-tertiary hover:bg-hover hover:text-text-primary"
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <form onSubmit={submit} className="space-y-3 px-5 py-4">
          {fields.map((f, i) => (
            <label key={f.name} className="block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">
                {f.label}
                {!f.required && <span className="ml-1 text-text-tertiary">(optional)</span>}
              </span>
              <input
                ref={i === 0 ? firstFieldRef : undefined}
                type={f.type ?? 'text'}
                required={f.required}
                autoComplete={f.autoComplete ?? 'off'}
                placeholder={f.placeholder}
                value={values[f.name] ?? ''}
                disabled={pending}
                onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
                className={cn(
                  'h-control w-full rounded-md border border-border-default bg-canvas px-3 text-sm outline-none',
                  'focus:border-ai focus:ring-2 focus:ring-ai/30 disabled:opacity-60',
                )}
              />
              {f.hint && <span className="mt-1 block text-[11px] text-text-tertiary">{f.hint}</span>}
            </label>
          ))}

          {error && (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={pending}>
              {pending ? 'Saving…' : submitLabel}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
